import { ZfPieChart } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useWealth } from '../../../../api';
import type { AccountType } from '../../../../api/generated/client';
import { useWealthContext } from '../../../../contexts/wealth-context';
import { useYwuApp } from '../../../../contexts/ywu-app-context';
import { getSeriesWeight } from '../../../../utils/chartUtils';
import { concatClean, formatPortfolioNumber } from '../../../../utils/utils';
import { EmptyChartMessage } from '../EmptyChartMessage';
import type { LegendPosition } from '../pieChartOptions';
import { getPieChartOptions } from '../pieChartOptions';

interface IProps {
    accountType: AccountType;
    width: number;
    showNetAmount: boolean;
    legendPosition: LegendPosition;
    floatLegend: boolean;
    legendWith: number;
    legendRtl?: boolean;
}

export const AssetsLiabilitiesChart: FunctionComponent<IProps> = ({
    accountType,
    width,
    showNetAmount,
    legendPosition,
    floatLegend,
    legendWith,
    legendRtl,
}) => {
    const { t } = useTranslation();

    const { activeLanguage: language } = useYwuApp();
    const { activeBalanceCategory, clientCurrency, wealthChartDataMap } = useWealthContext();
    const { assets, assetsTotal, liabilitiesTotal } = useWealth();

    const chartData = wealthChartDataMap[accountType];
    const hasActiveBalanceCategory = !!activeBalanceCategory;
    const biggestTotalAmount = liabilitiesTotal > assetsTotal ? liabilitiesTotal : assetsTotal;

    if (showNetAmount && !assets?.totalAmount) return <EmptyChartMessage />;

    const dataSeries = hasActiveBalanceCategory
        ? activeBalanceCategory.entries.map((asset, i) => ({
              name: concatClean(asset.name!, formatPortfolioNumber(asset.portfolioNumber, asset.businessRelationId, asset.portfolioIdentifier)),
              y: asset.referenceAmount,
              weight: asset.ratioTotalCategory,
              color: chartData?.chartColors[i],
          }))
        : chartData?.visibleCategories.map((category, i) => ({
              name: category.name[language],
              y: category.totalAmount,
              weight: showNetAmount
                  ? getSeriesWeight(category.ratioTotalAccount, biggestTotalAmount, category.totalAmount, chartData.netAmount)
                  : category.ratioTotalAccount,
              color: chartData.chartColors[i],
          }));

    if (showNetAmount && chartData && chartData.netAmount > 0) {
        dataSeries?.push({
            color: '#E5E5E5',
            name: t(chartData.netTitleTranslationKey),
            weight: chartData.netRatio,
            y: chartData.netAmount,
        });
    }

    // text in the center of the chart and the legend title
    const chartTitle = !hasActiveBalanceCategory && chartData ? t(chartData.titleTranslationKey) : activeBalanceCategory?.name[language];
    // amount displayed in the center of the chart
    const chartAmount = !hasActiveBalanceCategory && chartData ? chartData.totalAmount : activeBalanceCategory?.totalAmount;

    const chartOptions = getPieChartOptions({
        legend: {
            floatLegend: floatLegend,
            legendPosition: legendPosition,
            title: { text: chartTitle ?? '' },
            legendRtl: legendRtl,
            width: legendWith,
        },
        seriesData: dataSeries,
        titleOptions: { amount: chartAmount ?? 0, currency: clientCurrency ?? '', title: chartTitle ?? '' },
        width: width,
    });

    return <ZfPieChart options={chartOptions} />;
};
